import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import activeFolderIcon from '../../../../_metronic/assets/images/svg/data-room/blue-folder-icon.svg'
import { useAuth } from '../../auth'
import { createAccess, getRecentUploadApi } from '../core/_requests'
import { NoDataPage } from '../../resources/components/NoDataPage'
import { getFileType } from '../../../core/_util'
import { useNavigate } from 'react-router-dom'
import { ColumnDef, ColumnFiltersState, FilterFn, flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table'
import { getLocation } from '../../onboarding/core/_requests'
import { RankingInfo, rankItem } from '@tanstack/match-sorter-utils'
import TagManager from 'react-gtm-module'
import { GTMEvent } from '../../../hooks/gtmHelpers'
import { Pagination, Table } from 'react-bootstrap'
import SkeletonRow from '../../digital-signing/components/SkeletonRow'
import { PageTitle } from '../../../../_metronic/layout/core'
import { RecordsKeeperAIOverviewBreadCrumbs } from '../core/_constants'

declare module '@tanstack/react-table' {
    interface FilterFns {
        fuzzy: FilterFn<unknown>
    }
    interface FilterMeta {
        itemRank: RankingInfo
    }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value)
    addMeta({ itemRank })
    return itemRank.passed
}

const RecentlyVisitedFiles = () => {
    const { formatMessage } = useIntl()
    const { companyId, currentUser } = useAuth()
    const navigate = useNavigate()
    const [recentUpload, setRecentUpload] = useState(false) //eslint-disable-line
    const [shareholders, setShareholders] = useState<any[]>([])
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
    const [globalFilter, setGlobalFilter] = useState('')
    const [pagination, setPagination] = useState<{
        currentPage: number
        pageSize: number
        totalCount: number
        nextPage: number
        prevPage: number | null
        totalPages: number
    }>({
        currentPage: 1,
        pageSize: 10,
        totalCount: 0,
        nextPage: 0,
        prevPage: null,
        totalPages: 0,
    })

    const getDeviceType = () => {
        const ua = navigator.userAgent

        if (
            /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile|Kindle|NetFront|Silk-Accelerated|(hpw|web)OS|Fennec|Minimo|Opera Mobi|Blazer|Dolfin|Dolphin|Skyfire|Zune/.test(
                ua
            )
        ) {
            return 'MOBILE'
        }
        return 'WEB'
    }

    const getOS = () => {
        const ua = navigator.userAgent
        if (/iPhone|iPad|iPod|Macintosh/.test(ua)) return 'APPLE'
        if (/Win/.test(ua)) return 'WINDOWS'
        if (/android/i.test(ua)) return 'ANDROID'
        return 'Unknown'
    }

    const createAccessLog = async (recordId: any) => {
        const os = getOS()
        const deviceTypeView = getDeviceType()
        const locationApiData = await getLocation()
        const city = locationApiData?.data.country_name
        const ipAddress = locationApiData?.data?.ip

        try {
            if (deviceTypeView && os && city && recordId && companyId && currentUser?.userId) {
                const {
                    data: { success },
                } = await createAccess(
                    recordId,
                    os,
                    deviceTypeView,
                    city,
                    ipAddress,
                    'CREATE',
                    companyId,
                    currentUser?.userId
                )
                if (success) { }
            }
        } catch (err) {
            console.log(err)
        }
    }

    useEffect(() => {
        fetchOverviewData()
    }, []) // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchOverviewData()
    }, [pagination.currentPage, pagination.pageSize])// eslint-disable-line react-hooks/exhaustive-deps
    const fetchOverviewData = async () => {
        if (!companyId) return
        setRecentUpload(true)
        try {
            const {
                data: { success, data },
            } = await getRecentUploadApi(companyId, "recentVisitedFiles", pagination.currentPage,
                pagination.pageSize)
            if (success) {
                setShareholders(data?.data?.recordKeeperFiles)
                setPagination((prev) => ({
                    ...prev,
                    currentPage: data.pagination?.currentPage,
                    pageSize: data.pagination?.pageSize,
                    totalCount: data.pagination?.totalRecords,
                    nextPage: data.pagination?.nextPage,
                    prevPage: data.pagination?.prevPage,
                    totalPages: data.pagination?.totalPages,
                }))
            }
        } catch (error) {
            console.error(error)
        } finally {
            setRecentUpload(false)
        }
    }

    const multiFieldGlobalFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
        const nameMatch = rankItem(`${row.original.documentName}`, value)
        // Determine which match to use based on rank comparison or other logic
        let bestMatch: RankingInfo | undefined
        if (nameMatch.passed) {
            bestMatch = nameMatch
        }

        // Only call addMeta if a best match is found
        if (bestMatch) {
            addMeta({ itemRank: bestMatch })
        }

        // Return whether the search term passed in either field
        return !!bestMatch
    }
    const handlePageChange = (pageNumber: number) => {
        setPagination((prev) => ({ ...prev, currentPage: pageNumber }))
    }
    const columns = React.useMemo<ColumnDef<any, any>[]>(() => [
        {
            accessorKey: 'documentName',
            id: 'documentName',
            header: 'Name',
            cell: (info) => {
                const folderPath = `${info.row.original.path?.structureClassificationLevelOne?.name}/${info.row.original.path?.structureClassificationLevelTwo?.name}/${info.row.original.path?.structureClassificationLevelThree}/${info.row.original.path?.structureClassificationLevelThreeQuartely ? `${info.row.original.path?.structureClassificationLevelThreeQuartely}/` : ''}${info.row.original.path?.structureClassificationLevelThreeMonthly ? `${info.row.original.path?.structureClassificationLevelThreeMonthly}/` : ''}${info.row.original.path?.structureClassificationLevelFour.name}`

                return (
                    <div className='d-flex align-items-center gap-2' >

                        <img
                            src={getFileType(info.row.original.fileName)}
                            alt={info.row.original.documentName}
                            width={24}
                            height={24}
                            className='mb-md-0 mb-2'
                        />
                        <div>
                            <p className='font-size-13 m-0'>
                                {info.row.original.documentName}
                            </p>
                            <span className="d-flex align-items-center gap-1">
                                <img
                                    src={activeFolderIcon}
                                    alt="folder-icon"
                                    height={14}
                                    width={14}
                                />
                                <p className="font-size-10 text-muted mb-0">
                                    {folderPath}
                                </p>
                            </span>

                        </div>
                    </div >)
            },
            filterFn: 'includesString',
        },
        {
            accessorKey: 'uploadedOn',
            id: 'uploadedOn',
            header: 'Action',
            cell: (info) => {
                return (
                    <div className='d-flex justify-content-end'>
                        <button className="btn bg-light custom-button-property d-flex align-items-center justify-content-center text-muted w-md-auto w-100 height-36" onClick={() => {
                            createAccessLog(info.row.original.recordId)
                            navigate(`/rk/records-and-files/files-details/${info.row.original.recordId}`)
                        }}>
                            {formatMessage({ id: 'View' })}
                        </button>
                    </div>
                )
            },
            filterFn: 'includesString',
        },

    ], []) // eslint-disable-line react-hooks/exhaustive-deps

    const table = useReactTable({
        data: shareholders,
        columns,
        filterFns: {
            fuzzy: fuzzyFilter,
        },
        manualPagination: true,
        rowCount: pagination.totalCount,
        state: {
            columnFilters,
            globalFilter,
        },
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        globalFilterFn: multiFieldGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    })
    return (
        <><PageTitle
            breadcrumbs={RecordsKeeperAIOverviewBreadCrumbs}
            description={formatMessage({ id: 'Recent Visited Files' })}
        >
            {formatMessage({ id: 'RecordsKeeper AI' })}
        </PageTitle>
            <div className='d-flex flex-md-row flex-column justify-content-between align-items-md-center gap-2 mb-4 px-md-6 px-4'>
                <h1 className='font-size-16 mb-0'>{formatMessage({ id: 'Recently Visited Files' })}</h1>
                <div className='d-flex gap-md-4 gap-2'>
                    <form className='align-items-center w-100 w-md-auto d-flex'>
                        <span className='svg-icon svg-icon-3 position-absolute ms-3'>
                            <svg
                                width='24'
                                height='24'
                                viewBox='0 0 24 24'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                                className='mh-50px'
                            >
                                <rect
                                    opacity='0.5'
                                    x='17.0365'
                                    y='15.1223'
                                    width='8.15546'
                                    height='2'
                                    rx='1'
                                    transform='rotate(45 17.0365 15.1223)'
                                    fill='currentColor'
                                ></rect>
                                <path
                                    d='M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z'
                                    fill='currentColor'
                                ></path>
                            </svg>
                        </span>
                        <DebouncedInput
                            value={globalFilter ?? ''}
                            onChange={(value) => {
                                setGlobalFilter(String(value))
                                if (String(value).length > 0) {
                                    TagManager.dataLayer({
                                        dataLayer: {
                                            event: GTMEvent.AdvisorMentorSearchSuccessful,
                                            action: 'normal',
                                            // metadata: {
                                            //     company_id: companyId,
                                            //     user_id: currentUser?.userId,
                                            //     email: currentUser?.email,
                                            //     search_value: value,
                                            // },
                                        },
                                    })
                                }
                            }}
                            className='bg-body form-control form-control-flush ps-10 rounded-2 font-size-13 font-weight-400'
                            placeholder='Search'
                        />
                    </form>
                    {/* <div className='h-100 border bg-white d-flex rounded'>
                        <button
                            className={clsx('text-clr88 btn btn-sm btn-flex fw-semibold py-2 font-size-12 height-36')}
                            id='fc-toogle'
                            title='Sort and Filter'
                            data-bs-toggle='tooltip'
                            data-bs-placement='left'
                            data-bs-dismiss='click'
                            data-bs-trigger='hover'
                            onClick={() => {
                                setRecentUpload(true)
                            }}
                        >
                            <KTSVG
                                path={toAbsoluteUrl('/media/icons/duotune/general/gen031.svg')}
                                className='svg-icon-6 svg-icon-muted me-1'
                            />
                            <span className='d-none d-md-block'>{formatMessage({ id: 'Filter' })}</span>
                            {/* {selectedShareholderTypeFilter || selectedShareholderSortTypeFilter ? '*' : ''} */}
                    {/* </button>
                    </div> */}
                </div>
            </div>
            <div className='px-md-6 px-4'>
                <div className='bg-white rounded-top p-4' style={{ overflowX: 'auto' }}>
                    {!recentUpload && (shareholders?.length === 0 || table.getRowModel().rows.length === 0) ? (
                        <div className='my-5 mx-6'>
                            <NoDataPage />
                        </div>
                    )
                        :
                        <Table className='w-100 mb-0' style={{ minWidth: '600px' }}>
                            <thead className='custom-thead border-bottom'>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <tr key={headerGroup.id} className='font-size-12 font-weight-400 table-row border-dark-mode'>
                                        {headerGroup.headers.map((header) => (
                                            <th key={header.id} colSpan={header.colSpan} className='min-w-120px pb-6 pt-0 px-4'>
                                                {header.isPlaceholder ? null : (
                                                    <div
                                                        className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                                                        onClick={header.column.getToggleSortingHandler()}
                                                    >
                                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                                        {{
                                                            asc: ' ▲',
                                                            desc: ' ▼',
                                                        }[header.column.getIsSorted() as string] ?? null}
                                                    </div>
                                                )}
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody>
                                {recentUpload ? (
                                    <>
                                        <SkeletonRow count={8} />
                                    </>
                                ) :
                                    <>
                                        {table.getRowModel().rows.map((row, index) => (
                                            <React.Fragment key={row.id}>
                                                <tr key={row.id} >
                                                    {row.getVisibleCells().map((cell, cellIndex) => (
                                                        <>
                                                            {cellIndex === 0 ? (
                                                                <td key={cell.id} className='pt-5 px-4 border-0'>
                                                                    <div className='d-flex gap-2'>
                                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                                    </div>
                                                                </td >
                                                            ) : (
                                                                <td key={cell.id} className='pt-5 px-4 border-0'>
                                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                                </td>
                                                            )}
                                                        </>
                                                    ))}
                                                </tr>
                                            </React.Fragment>
                                        ))}
                                    </>}

                            </tbody>
                        </Table >
                    }
                </div >
                {!recentUpload && shareholders?.length > 0 && (
                    <div className='w-100 d-md-flex justify-content-between align-items-center px-0 mt-8'>
                        <div className='d-flex gap-2 w-100 align-items-center justify-content-md-start justify-content-center mb-4'>
                            <select
                                aria-label='Default select example'
                                className='p-4 py-3 w-80px border border-1 rounded-2 font-size-13'
                                value={pagination.pageSize}
                                onChange={(e) => {
                                    const newSize = Number(e.target.value)
                                    setPagination((prev) => ({
                                        ...prev,
                                        pageSize: newSize,
                                        pageIndex: 0, // Reset to first page when page size changes
                                    }))
                                }}
                            >
                                {[10, 20, 30].map((pageSize) => (
                                    <option key={pageSize} value={pageSize}>
                                        {pageSize}
                                    </option>
                                ))}
                            </select>

                            <span className='font-size-13 text-center text-md-start'>
                                {formatMessage({
                                    id: `Showing ${pagination.currentPage === 1
                                        ? 1
                                        : (pagination.currentPage - 1) * pagination.pageSize + 1
                                        } to ${pagination.totalCount > pagination.currentPage * pagination.pageSize
                                            ? pagination.currentPage * pagination.pageSize
                                            : pagination.totalCount -
                                            pagination.currentPage * pagination.pageSize +
                                            pagination.currentPage * pagination.pageSize
                                        } of ${pagination.totalCount} records`,
                                })}
                            </span>
                        </div>

                        <Pagination className='w-md-100 w-auto d-flex flex-md-end justify-content-center justify-content-md-end '>
                            <Pagination.First
                                onClick={() => handlePageChange(1)}
                                disabled={pagination.prevPage === null}
                            >
                                {'<<'}
                            </Pagination.First>

                            <Pagination.Prev
                                onClick={() => handlePageChange(pagination.currentPage - 1)}
                                disabled={pagination.prevPage === null}
                            >
                                {'<'}
                            </Pagination.Prev>

                            {Array.from({ length: 3 }, (_, i) => {
                                const pageNumber = pagination.currentPage + i;
                                return (
                                    pageNumber <= pagination.totalPages && (
                                        <Pagination.Item
                                            key={pageNumber}
                                            active={pageNumber === pagination.currentPage}
                                            onClick={() => handlePageChange(pageNumber)}
                                        >
                                            {pageNumber}
                                        </Pagination.Item>
                                    )
                                );
                            })}

                            <Pagination.Next
                                onClick={() => handlePageChange(pagination.currentPage + 1)}
                                disabled={!pagination.nextPage}
                            >
                                {'>'}
                            </Pagination.Next>

                            <Pagination.Last
                                onClick={() => handlePageChange(pagination.totalPages)}
                                disabled={!pagination.nextPage}
                            >
                                {'>>'}
                            </Pagination.Last>
                        </Pagination>
                    </div>
                )}
            </div>
        </>
    )
}
function DebouncedInput({
    value: initialValue,
    onChange,
    debounce = 500,
    ...props
}: {
    value: string | number
    onChange: (value: string | number) => void
    debounce?: number
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
    const [value, setValue] = useState(initialValue)
    useEffect(() => {
        setValue(initialValue)
    }, [initialValue])

    useEffect(() => {
        const timeout = setTimeout(() => {
            onChange(value)
        }, debounce)

        return () => clearTimeout(timeout)
    }, [value, debounce, onChange])
    return <input {...props} value={value} onChange={(e) => setValue(e.target.value)} />
}

export default RecentlyVisitedFiles
